/**
 * Sandbox URL rewriter for the demo harness.
 *
 * Wraps the real `buildUrl` from the extension and rewrites every
 * hellopearl.com production AND dev hostname to its `sandbox.*` equivalent
 * so the demo only ever loads sandbox.secondopinion, sandbox.pi,
 * sandbox.precheck and sandbox.voice in iframes / new tabs.
 *
 * Also exports the sandbox-specific Voice URL and login popup URL the
 * demo shell uses in place of the production constants.
 */

import { buildUrl as realBuildUrl, type BuildUrlContext } from '../../src/pearl-shell/url-builder';
import type { PearlAction } from '../../src/pearl-shell/icon-config';

/**
 * Map from production hellopearl hostnames to their sandbox subdomain.
 * Order matters: longer matches must appear before shorter ones because
 * we use simple substring `replace`. None of these prefixes overlap, but
 * we still iterate the entries explicitly for clarity.
 */
const REWRITE_MAP: ReadonlyArray<[string, string]> = [
  ['dev.secondopinion.hellopearl.com', 'sandbox.secondopinion.hellopearl.com'],
  ['secondopinion.hellopearl.com', 'sandbox.secondopinion.hellopearl.com'],
  ['dev.pi.hellopearl.com', 'sandbox.pi.hellopearl.com'],
  ['pi.hellopearl.com', 'sandbox.pi.hellopearl.com'],
  ['dev.precheck.hellopearl.com', 'sandbox.precheck.hellopearl.com'],
  ['precheck.hellopearl.com', 'sandbox.precheck.hellopearl.com'],
  ['dev.voice.hellopearl.com', 'sandbox.voice.hellopearl.com'],
  ['voice.hellopearl.com', 'sandbox.voice.hellopearl.com'],
];

/** Sandbox Voice URL used by the embedded Voice widget (and the Voice action). */
export const SANDBOX_VOICE_URL =
  'https://sandbox.voice.hellopearl.com/?embedded=true&startExpanded=true';

/** Sandbox login popup URL — replaces `https://secondopinion.hellopearl.com/`. */
export const SANDBOX_LOGIN_URL = 'https://sandbox.secondopinion.hellopearl.com/';

/** Sandbox login base — replaces `LOGIN_BASE` from the extension url-builder. */
export const SANDBOX_LOGIN_BASE = 'https://sandbox.secondopinion.hellopearl.com';

/** Sandbox Second Opinion base. */
export const SANDBOX_SO_BASE = 'https://sandbox.secondopinion.hellopearl.com';

/**
 * Demo harness Second Opinion landing when no patient ID is set in the
 * toolbar (same host as production `SO_BASE`, sandbox-rewritten).
 */
export const DEMO_SECOND_OPINION_URL =
  'https://sandbox.secondopinion.hellopearl.com/#/v2/pearlsandbox/';

/** Production Second Opinion base — `rewriteToSandbox` maps to sandbox host. */
const SO_PROD_BASE = 'https://secondopinion.hellopearl.com';

/** Sandbox precheck base — used as the insurance override. */
export const SANDBOX_PRECHECK_BASE = 'https://sandbox.precheck.hellopearl.com';

/**
 * Rewrites any hellopearl.com URL to its sandbox equivalent. Already-sandboxed
 * URLs pass through unchanged because the production hostnames they contain
 * (e.g. `sandbox.secondopinion.hellopearl.com`) only match
 * `secondopinion.hellopearl.com` as a suffix, which is fine because the
 * replacement is a no-op when run a second time on a result that already
 * contains `sandbox.secondopinion.hellopearl.com`.
 *
 * @param url - The URL to rewrite. Non-hellopearl URLs pass through.
 * @returns The rewritten URL with sandbox subdomains.
 *
 * @example
 *   rewriteToSandbox('https://secondopinion.hellopearl.com/schedule')
 *   // → 'https://sandbox.secondopinion.hellopearl.com/schedule'
 */
export function rewriteToSandbox(url: string): string {
  let out = url;
  for (const [from, to] of REWRITE_MAP) {
    // Guard against double-rewrites: only replace if the target sandbox host
    // is not already present at the same position.
    if (out.includes(to)) continue;
    out = out.split(from).join(to);
  }
  return out;
}

/**
 * Sandbox-aware drop-in replacement for `buildUrl` from the extension.
 * Calls the real `buildUrl` and rewrites the result through `rewriteToSandbox`.
 *
 * @param action - One of the leaf action ids defined in `icon-config.ts`.
 * @param ctx - Patient/office/path context, plus optional Voice URL.
 * @returns The sandbox-rewritten URL, or `null` if `buildUrl` returned `null`.
 */
export function buildUrl(action: PearlAction, ctx: BuildUrlContext): string | null {
  // Demo page is never a Denticon "patient page", so the real `buildUrl`
  // would never embed `patientId` for tooth. When the toolbar supplies a
  // patient ID, mirror production: `/#/disambiguation/{id}` on Second Opinion
  // (then sandbox-rewrite the host). Otherwise open the fixed pearlsandbox
  // deep link.
  if (action === 'tooth') {
    const pid = ctx.patientId?.trim() || null;
    if (pid) {
      return rewriteToSandbox(`${SO_PROD_BASE}/#/disambiguation/${pid}`);
    }
    return DEMO_SECOND_OPINION_URL;
  }
  if (action === 'patient-report') {
    const pid = ctx.patientId?.trim() || null;
    if (pid) {
      const params = new URLSearchParams({ pearl_dock: 'patient-report' });
      const oid = ctx.officeId?.trim() || null;
      if (oid) params.set('office_id', oid);
      return rewriteToSandbox(
        `${SO_PROD_BASE}/#/disambiguation/${encodeURIComponent(pid)}?${params.toString()}`,
      );
    }
    return DEMO_SECOND_OPINION_URL;
  }
  // Force the insurance override to the sandbox precheck base so a non-PI
  // org with insurance still routes through the sandbox precheck domain.
  const sandboxedCtx: BuildUrlContext = {
    ...ctx,
    voiceUrl: ctx.voiceUrl ? rewriteToSandbox(ctx.voiceUrl) : SANDBOX_VOICE_URL,
    insuranceUrlOverride: ctx.insuranceUrlOverride
      ? rewriteToSandbox(ctx.insuranceUrlOverride)
      : undefined,
  };
  const url = realBuildUrl(action, sandboxedCtx);
  return url ? rewriteToSandbox(url) : null;
}
